import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get the app domain for shareable links (e.g., "artha.link")
 * Uses NEXT_PUBLIC_APP_DOMAIN env variable, defaults to "artha.link"
 */
export function getAppDomain(): string {
  const raw = process.env.NEXT_PUBLIC_APP_DOMAIN || "artha.link";
  // Strip protocol prefix so the domain can be safely prefixed with https:// by callers
  return raw.replace(/^https?:\/\//, "");
}

export function getInviteDomain(): string {
  const raw = process.env.NEXT_PUBLIC_INVITE_DOMAIN || "invite.arthalink.info";
  return raw.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

export function getAppBaseUrl(): string {
  return (
    process.env.NEXT_PUBLIC_APP_URL ||
    process.env.NEXT_PUBLIC_SITE_URL ||
    (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : undefined) ||
    "https://artha.link"
  ).replace(/\/$/, "");
}

/**
 * Get the portal/app base URL (e.g., "https://my.artha.link")
 * Used for login, dashboard, and other authenticated areas.
 */
export function getPortalBaseUrl(): string {
  const envUrl = process.env.NEXT_PUBLIC_PORTAL_URL || process.env.NEXT_PUBLIC_APP_URL;
  if (envUrl) {
    try {
      const url = new URL(envUrl);
      if (url.hostname === "artha.link") {
        url.hostname = "my.artha.link";
        return url.toString().replace(/\/$/, "");
      }
    } catch {
      // Fallback if envUrl is not a valid absolute URL
    }
    return envUrl.replace(/\/$/, "");
  }
  return "https://my.artha.link";
}

export function buildAppLoginUrl(
  params?: Record<string, string | undefined>,
  baseUrl?: string
): string {
  const url = new URL("/login", baseUrl || getPortalBaseUrl());
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
}

export function generateCreatorInviteUrl(inviteCode?: string | null): string {
  if (!inviteCode) {
    return `https://${getAppDomain()}`;
  }
  return `https://${getInviteDomain()}/${encodeURIComponent(inviteCode)}`;
}

/**
 * Build a shareable profile link
 * @param username - The user's username (with or without @)
 * @returns Full shareable link like "artha.link/@username"
 */
export function getShareableLink(username?: string | null): string {
  const domain = getAppDomain();
  if (!username) return domain;
  const cleanUsername = username.startsWith("@") ? username : `@${username}`;
  return `${domain}/${cleanUsername}`;
}

/**
 * Build a shareable profile link without @ prefix
 * @param username - The user's username
 * @returns Full shareable link like "artha.link/username"
 */
export function getShareableLinkNoAt(username?: string | null): string {
  const domain = getAppDomain();
  if (!username) return domain;
  return `${domain}/${username}`;
}

const safeDecodeURIComponent = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const normalizeUsernameParam = (value?: string | null) => {
  if (!value) return "";
  const decodedOnce = safeDecodeURIComponent(value);
  const decodedTwice = safeDecodeURIComponent(decodedOnce);
  const resolved = decodedTwice !== decodedOnce ? decodedTwice : decodedOnce;
  const trimmed = resolved.trim();
  return trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
};

export const capitalizeFirst = (value?: string | null) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

const SEO_SUFFIX = " | artha.link";

export const appendSeoSuffix = (value?: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.endsWith(SEO_SUFFIX) ? trimmed : `${trimmed}${SEO_SUFFIX}`;
};

const SALARY_CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  CAD: "CA$",
  CHF: "CHF",
  AUD: "A$",
  MXN: "MX$",
  BDT: "৳",
  BGN: "лв",
  INR: "₹",
  PHP: "₱",
  SGD: "S$",
};

const formatSalaryValue = (value: number) => {
  if (value >= 1000) {
    const normalized = value / 1000;
    const formatted = Number.isInteger(normalized)
      ? String(normalized)
      : normalized.toFixed(1).replace(/\.0$/, "");
    return `${formatted}K`;
  }
  return String(value);
};

const getSalaryCurrencyPrefix = (currency?: string | null) => {
  if (!currency) return "";
  const normalized = currency.toUpperCase();
  const symbol = SALARY_CURRENCY_SYMBOLS[normalized];
  if (!symbol) return `${normalized} `;
  if (/^[A-Z]+$/.test(symbol)) return `${symbol} `;
  return symbol;
};

export const formatSalary = (
  job: {
    salary_min?: number | null;
    salary_max?: number | null;
    salary_curr?: string | null;
  },
  options?: { lpaLabel?: string }
) => {
  const min = job.salary_min;
  const max = job.salary_max;
  const currency = getSalaryCurrencyPrefix(job.salary_curr);

  const suffix = options?.lpaLabel ? ` ${options.lpaLabel}` : "";

  if (typeof min === "number" && typeof max === "number") {
    return `${currency}${formatSalaryValue(min)}-${formatSalaryValue(max)}${suffix}`;
  }
  if (typeof min === "number") {
    return `${currency}${formatSalaryValue(min)}${suffix}`;
  }
  if (typeof max === "number") {
    return `${currency}${formatSalaryValue(max)}${suffix}`;
  }
  return "";
};

const getDefinedNumbers = (
  values: Array<number | null | undefined>
): number[] => values.filter((value): value is number => typeof value === "number");

const allZeros = (values: number[]): boolean => values.length > 0 && values.every((value) => value === 0);

export const shouldHideSalaryBadge = (job: {
  salary_min?: number | null;
  salary_max?: number | null;
  exp_min?: number | null;
  exp_max?: number | null;
}) => {
  const salaryValues = getDefinedNumbers([job.salary_min, job.salary_max]);
  const experienceValues = getDefinedNumbers([job.exp_min, job.exp_max]);
  const isSalaryZero = allZeros(salaryValues);
  const isExperienceZero = allZeros(experienceValues);
  return isSalaryZero || isExperienceZero;
};

/**
 * Format currency amount with proper symbol and locale
 * @param amount - The amount to format
 * @param currency - Currency code (USD, INR, etc.)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount?: number | null, currency = "USD"): string => {
  const safeAmount = amount ?? 0;
  const locale = currency === "INR" ? "en-IN" : "en-US";
  const symbol = currency === "INR" ? "₹ " : "$";

  return `${symbol}${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeAmount)}`;
};

/**
 * Calculate growth percentage between two values
 * @param current - Current value
 * @param previous - Previous value
 * @returns Formatted growth percentage string
 */
export const calculateGrowth = (current?: number | null, previous?: number | null): string => {
  const safeCurrent = current ?? 0;
  const safePrevious = previous ?? 0;

  if (safePrevious === 0) return "+0%";
  const growth = ((safeCurrent - safePrevious) / safePrevious) * 100;
  return `${growth > 0 ? "+" : ""}${growth.toFixed(1)}%`;
};

/**
 * Generate a job share URL for a specific creator and job
 * @param creatorHandle - The creator's handle/username
 * @param jobSlug - The job's slug
 * @returns Full job URL like "https://domain.com/username/jobs/job-slug"
 */
export const generateJobShareUrl = (creatorHandle?: string | null, jobSlug?: string | null): string => {
  if (!creatorHandle || !jobSlug) return "";
  const handle = creatorHandle.startsWith("@") ? creatorHandle : `@${creatorHandle}`;
  // Use canonical app URL from env — never window.location.origin,
  // which returns my.artha.link on creator-side pages
  return `${getAppBaseUrl()}/${handle}/jobs/${encodeURIComponent(jobSlug)}`;
};

/**
 * Truncate description text for meta descriptions
 * @param text - The text to truncate
 * @param maxLength - Maximum length (default: 160 for SEO)
 * @returns Truncated text with ellipsis if needed
 */
export const getDescriptionPreview = (text?: string | null, maxLength = 160): string => {
  if (!text) return "";
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 3).trim()}...`;
};

/**
 * Build a Next image optimizer URL for remote/local images when we want a
 * lighter cached rendition but still render with a plain <img>.
 */
export const getOptimizedImageUrl = (
  src?: string | null,
  options: { width?: number; quality?: number } = {}
): string => {
  if (!src) return "";

  const trimmed = src.trim();
  if (!trimmed) return "";

  // Keep data URLs and already-optimized URLs unchanged.
  if (
    trimmed.startsWith("data:") ||
    trimmed.startsWith("/_next/image")
  ) {
    return trimmed;
  }

  const width = options.width ?? 160;
  const quality = options.quality ?? 75;
  return `/_next/image?url=${encodeURIComponent(trimmed)}&w=${width}&q=${quality}`;
};

/**
 * Detect if the current device is a mobile device based on user agent and screen width
 * @returns boolean - true if mobile device
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) ||
    window.innerWidth < 1024 ||
    navigator.maxTouchPoints > 0
  );
};
